/**
 * Viewer Settings Page
 * Allows viewers to change email and password
 * Staff (Admin/Manager/Streamer) should use /staff-settings instead
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';
import { ROUTES } from '../lib/constants';
import { localizedApiMessage, type LocalizedMessage } from '../lib/api-error-message';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GuideLink from '../components/GuideLink';

export default function Settings() {
    const { t } = useTranslation('common');
    const { t: tGuide } = useTranslation('guide');
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Redirect staff to /staff-settings
    const isStaff = user?.role && ['ADMIN', 'MANAGER', 'STREAMER'].includes(user.role);

    useEffect(() => {
        if (isStaff) {
            navigate(ROUTES.STAFF_SETTINGS, { replace: true });
        }
    }, [isStaff, navigate]);

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState<LocalizedMessage | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState<LocalizedMessage | null>(null);

    // Password visibility state
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Email change state
    const [newEmail, setNewEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState<LocalizedMessage | null>(null);
    const [emailSuccess, setEmailSuccess] = useState<LocalizedMessage | null>(null);
    const [withdrawalOpen, setWithdrawalOpen] = useState(false);
    const [withdrawalPassword, setWithdrawalPassword] = useState('');
    const [pointsForfeitConfirmed, setPointsForfeitConfirmed] = useState(false);
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);
    const [withdrawalError, setWithdrawalError] = useState<LocalizedMessage | null>(null);

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError(null);
        setPasswordSuccess(null);

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError({ key: 'settings.requiredFieldsError' });
            return;
        }

        // Validation - Viewers require 8+ chars
        if (newPassword.length < 8) {
            setPasswordError({ key: 'settings.password.minLengthError' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError({ key: 'settings.password.mismatchError' });
            return;
        }

        setPasswordLoading(true);
        try {
            // Viewers use /api/auth/viewer/change-password
            await apiClient.viewerChangePassword(currentPassword, newPassword);
            setPasswordSuccess({ key: 'settings.password.success' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: unknown) {
            setPasswordError(localizedApiMessage(err, 'apiErrors.settingsFailed'));
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleEmailChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailError(null);
        setEmailSuccess(null);

        if (!newEmail.trim() || !emailPassword) {
            setEmailError({ key: 'settings.requiredFieldsError' });
            return;
        }

        // Basic email validation
        if (!newEmail.includes('@')) {
            setEmailError({ key: 'settings.email.invalidError' });
            return;
        }

        setEmailLoading(true);
        try {
            await apiClient.viewerChangeEmail(newEmail, emailPassword);
            setEmailSuccess({ key: 'settings.email.verificationSent' });
            setNewEmail('');
            setEmailPassword('');
        } catch (err: unknown) {
            setEmailError(localizedApiMessage(err, 'apiErrors.settingsFailed'));
        } finally {
            setEmailLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate(ROUTES.LOGIN);
    };

    const handleWithdrawal = async (e: React.FormEvent) => {
        e.preventDefault();
        setWithdrawalError(null);
        if (!withdrawalPassword || !pointsForfeitConfirmed) {
            setWithdrawalError({ key: 'settings.withdrawal.requiredError' });
            return;
        }
        setWithdrawalLoading(true);
        try {
            await apiClient.viewerWithdraw(withdrawalPassword, true);
            logout();
            navigate(ROUTES.LOGIN);
        } catch (err: unknown) {
            setWithdrawalError(localizedApiMessage(err, 'settings.withdrawal.error'));
        } finally {
            setWithdrawalLoading(false);
        }
    };

    // Don't render if staff (will redirect)
    if (isStaff) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Bar */}
            <header className="bg-accent text-white shadow-md">
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl font-bold flex items-center">
                            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {t('settings.title')}
                        </h1>
                        <div className="flex items-center gap-4">
                            <div className="text-sm">
                                <span className="opacity-90">{t('header.loggedInAs')}:</span>{' '}
                                <span className="font-semibold">{user?.username}</span>
                            </div>
                            <div className="flex gap-2">
                                <LanguageSwitcher variant="buttons" />
                                <Link
                                    to={ROUTES.WATCH}
                                    className="bg-accent-50 text-accent-700 hover:bg-accent-100 text-sm font-semibold py-2 px-4 rounded transition-colors shadow-sm"
                                >
                                    {t('navigation.backToWatch')}
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded transition-colors"
                                >
                                    {t('buttons.logout')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto space-y-8">
                    {/* Password Change Section */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {t('settings.password.title')}
                        </h2>
                        <form noValidate onSubmit={handlePasswordChange} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.password.current')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        aria-label={showCurrentPassword ? t('password.hide') : t('password.show')}
                                    >
                                        {showCurrentPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.password.new')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        aria-label={showNewPassword ? t('password.hide') : t('password.show')}
                                    >
                                        {showNewPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('settings.password.minLength')}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.password.confirm')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                        aria-label={showConfirmPassword ? t('password.hide') : t('password.show')}
                                    >
                                        {showConfirmPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            {passwordError && (
                                <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{t(passwordError.key, passwordError.values)}</div>
                            )}
                            {passwordSuccess && (
                                <div className="text-green-500 text-sm">{t(passwordSuccess.key, passwordSuccess.values)}</div>
                            )}
                            <button
                                type="submit"
                                disabled={passwordLoading}
                                className="w-full bg-pink-500 text-white py-2 px-4 rounded-md hover:bg-pink-600 transition-colors disabled:opacity-50"
                            >
                                {passwordLoading ? t('buttons.saving') : t('settings.password.change')}
                            </button>
                        </form>
                    </div>

                    {/* Email Change Section */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {t('settings.email.title')}
                        </h2>
                        <form noValidate onSubmit={handleEmailChange} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.email.new')}
                                </label>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('settings.email.passwordConfirm')}
                                </label>
                                <input
                                    type="password"
                                    value={emailPassword}
                                    onChange={(e) => setEmailPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('settings.email.passwordNote')}</p>
                            </div>
                            {emailError && (
                                <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{t(emailError.key, emailError.values)}</div>
                            )}
                            {emailSuccess && (
                                <div className="text-green-500 text-sm">{t(emailSuccess.key, emailSuccess.values)}</div>
                            )}
                            <button
                                type="submit"
                                disabled={emailLoading}
                                className="w-full bg-pink-500 text-white py-2 px-4 rounded-md hover:bg-pink-600 transition-colors disabled:opacity-50"
                            >
                                {emailLoading ? t('buttons.saving') : t('settings.email.change')}
                            </button>
                        </form>
                    </div>

                    <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                        <h2 className="mb-2 text-xl font-bold text-red-800">{t('settings.withdrawal.title')}</h2>
                        <p className="mb-4 text-sm text-red-700">{t('settings.withdrawal.description')}</p>
                        {!withdrawalOpen ? (
                            <button type="button" onClick={() => setWithdrawalOpen(true)} className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700">
                                {t('settings.withdrawal.start')}
                            </button>
                        ) : (
                            <form noValidate onSubmit={handleWithdrawal} className="space-y-4">
                                <label className="block text-sm font-medium text-red-900">
                                    {t('settings.withdrawal.password')}
                                    <input type="password" value={withdrawalPassword} onChange={(e) => setWithdrawalPassword(e.target.value)} className="mt-1 w-full rounded border border-red-300 px-3 py-2 text-gray-900" />
                                </label>
                                <label className="flex items-start gap-2 text-sm text-red-900">
                                    <input type="checkbox" checked={pointsForfeitConfirmed} onChange={(e) => setPointsForfeitConfirmed(e.target.checked)} />
                                    {t('settings.withdrawal.consent')}
                                </label>
                                {withdrawalError && <div role="alert" className="text-sm text-red-700">{t(withdrawalError.key, withdrawalError.values)}</div>}
                                <button type="submit" disabled={withdrawalLoading} className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50">
                                    {withdrawalLoading ? t('buttons.saving') : t('settings.withdrawal.confirm')}
                                </button>
                            </form>
                        )}
                        <GuideLink chapter="withdrawal" className="ml-4 mt-4 inline-block text-sm font-medium text-red-700 underline hover:text-red-900">
                            {tGuide('links.withdrawal')}
                        </GuideLink>
                    </div>

                    {/* Back navigation */}
                    <div className="text-center">
                        <GuideLink chapter="settings" className="mb-3 block text-sm font-medium text-pink-500 hover:text-pink-600">
                            {tGuide('links.settings')}
                        </GuideLink>
                        <Link
                            to={ROUTES.WATCH}
                            className="text-pink-500 hover:text-pink-600 font-medium"
                        >
                            {t('navigation.backToWatch')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
